import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Search, Trash2, FileText, Send, CheckCircle2, Download, Upload, CreditCard, Copy, ChevronDown } from 'lucide-react';
import { apiInvoices, apiCustomers, payments, email, invoiceActions } from '../../lib/api';
import http from '../../lib/api';
import { fmt, fmtDateUS, nextNumber, invoiceOutstanding, invoicePaid, invoiceTotal } from '../../lib/invoiceItems';
import InvoiceFormDialog from '../forms/InvoiceFormDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { toCSV, downloadFile, parseCSV, readFileAsText } from '../../lib/importExport';
import BulkBar, { TickBox } from '../ui/bulk-bar';
import useSelection from '../../lib/useSelection';
import { toast } from 'sonner';

import { normDate } from '../../lib/dates';
const statusBadge = (s) => ({ Paid: 'bg-emerald-100 text-emerald-700', Open: 'bg-blue-100 text-blue-700', Sent: 'bg-indigo-100 text-indigo-700', Overdue: 'bg-red-100 text-red-700', Draft: 'bg-slate-100 text-slate-700' }[s] || 'bg-slate-100 text-slate-700');
const STATUS_OPTIONS = ['Draft', 'Sent', 'Open', 'Paid', 'Overdue'];

export default function Invoices() {
  // The search in the top bar sends an invoice here by number, so the page
  // opens already filtered to it rather than on the whole book.
  const [params] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState(params.get('q') || '');
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    apiInvoices.list().then((d) => setInvoices(d || [])).catch(() => setInvoices([]));
    apiCustomers.list().then((d) => setCustomers(d || [])).catch(() => setCustomers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const custName = (inv) => inv.customerName || customers.find((c) => c.id === inv.customerId)?.name || 'Unknown';
  // An invoice is looked up by the number printed on it as often as by the
  // customer, and an imported invoice carries that number under `number` or
  // `invoiceNumber` rather than as its id — so matching the id alone made a
  // search for a real invoice number come back empty.
  const haystack = (i) => [custName(i), i.id, i.number, i.invoiceNumber,
                           i.customerAccountNumber, i.customerAccount]
    .filter(Boolean).join(' ').toLowerCase();
  const filtered = invoices.filter((i) => {
    if (q && !haystack(i).includes(q.trim().toLowerCase())) return false;
    if (filter !== 'all' && i.status !== filter) return false;
    return true;
  }).sort((a, b) => normDate(b.date).localeCompare(normDate(a.date)) || (Number(b.number) || 0) - (Number(a.number) || 0));
  // What the table paints, named once so the tick boxes and the rows cannot
  // disagree about which invoices are on screen.
  const shown = filtered.slice(0, 300);
  const totals = {
    open: invoices.filter((i) => i.status === 'Open').reduce((a, i) => a + invoiceOutstanding(i), 0),
    overdue: invoices.filter((i) => i.status === 'Overdue').reduce((a, i) => a + invoiceOutstanding(i), 0),
    paid: invoices.filter((i) => i.status === 'Paid').reduce((a, i) => a + invoiceTotal(i), 0),
  };

  const onDelete = async (id) => { try { await apiInvoices.remove(id); load(); toast.success('Invoice deleted'); } catch { toast.error('Failed'); } };
  const onMarkPaid = async (inv) => {
    // Only what is still owed is new money; a part-paid invoice already has the rest.
    const owed = invoiceOutstanding(inv);
    try {
      // The payment is recorded first, so an invoice is never marked Paid without one.
      if (owed > 0) {
        await http.post('/payments', { id: `PAY-${inv.number || inv.id}-${Date.now().toString(36)}`, invoiceNumber: inv.number || inv.id, invoiceId: inv.id, date: fmtDateUS(new Date()), status: 'Success', method: 'manual', amount: owed, customer: custName(inv), customerAccountNumber: inv.customerAccountNumber || '', account: 'Operating Checking', type: 'Deposit', matched: true });
      }
      await apiInvoices.update(inv.id, { ...inv, status: 'Paid', paid: invoicePaid(inv) + owed, amountDue: 0, balance: 0 });
      const cust = customers.find((c) => c.id === inv.customerId);
      if (cust) await apiCustomers.update(cust.id, { ...cust, balance: Math.max(0, (Number(cust.balance) || 0) - owed) }).catch(() => {});
      load(); toast.success(owed > 0 ? `Recorded ${fmt(owed)} and marked paid` : 'Marked as paid');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record the payment'); }
  };
  const onCharge = async (inv) => {
    try { const { url } = await payments.createSession(inv.id); window.location.href = url; }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not start card payment'); }
  };
  const onSend = async (inv) => {
    const t = toast.loading(`Emailing ${inv.id}…`);
    try { const r = await email.sendInvoice(inv.id); toast.success(`Emailed to ${r.to}`, { id: t }); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send', { id: t }); }
  };
  const onDuplicate = async (inv) => {
    try { const copy = await invoiceActions.duplicate(inv.id); load(); toast.success(`Duplicated as ${copy.id}`); }
    catch { toast.error('Could not duplicate'); }
  };

  // ---- bulk selection ----
  // The picked ids used to outlive the filter: tick five rows, switch the status
  // filter, press Delete, and invoices nobody could see went with them. The
  // shared picker only ever hands back rows the table is showing.
  const sel = useSelection(shown.map((i) => i.id));
  const pickedTotal = Math.round(shown
    .filter((i) => sel.has(i.id))
    .reduce((sum, i) => sum + (Number(i.total) || 0), 0) * 100) / 100;
  const bulkStatus = async (status) => {
    try { const r = await invoiceActions.bulkUpdate(sel.chosen, status); sel.clear(); load(); toast.success(`${r.updated} invoice(s) set to ${status}`); }
    catch { toast.error('Bulk update failed'); }
  };
  const bulkDuplicate = async () => {
    const ids = sel.chosen;
    try { for (const id of ids) { await invoiceActions.duplicate(id); } sel.clear(); load(); toast.success(`Duplicated ${ids.length} invoice(s)`); }
    catch { toast.error('Some duplicates failed'); load(); }
  };
  const bulkDelete = async () => {
    const ids = sel.chosen;
    if (!window.confirm(`Delete ${ids.length} selected invoice(s)? This cannot be undone.`)) return;
    try { for (const id of ids) { await apiInvoices.remove(id); } sel.clear(); load(); toast.success(`Deleted ${ids.length} invoice(s)`); }
    catch { toast.error('Some deletes failed'); load(); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-invoices">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Invoices</h2><p className="text-slate-500 text-sm">Create and track customer invoices.</p></div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="acct-new-invoice-btn"><Plus className="w-4 h-4" />New Invoice</Button>
      </div>

      <div className="flex gap-2 -mt-2">
        <label className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-slate-200 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
          <Upload className="w-3.5 h-3.5" />Import CSV
          <input type="file" accept=".csv" className="hidden" onChange={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            const parsed = parseCSV(await readFileAsText(f));
            const start = nextNumber(invoices, 6000);
            const docs = parsed.map((r, i) => ({ id: r.Invoice || `INV-${start + i}`, number: String(start + i), customerName: r.Customer || '', date: r.Date || fmtDateUS(new Date()), due: r.Due || fmtDateUS(new Date()), status: r.Status || 'Open', items: [{ desc: 'Imported', qty: 1, rate: Number(r.Amount) || 0 }], total: Number(r.Amount) || 0, paid: r.Status === 'Paid' ? Number(r.Amount) || 0 : 0 }));
            try { await apiInvoices.bulk(docs); load(); toast.success(`Imported ${docs.length} invoices`); } catch { toast.error('Import failed'); }
            e.target.value = '';
          }} />
        </label>
        <Button size="sm" variant="outline" onClick={() => {
          const rows = invoices.map((i) => ({ ...i, customer: custName(i) }));
          downloadFile('xpertpests-invoices.csv', toCSV(rows, [{ key: 'id', header: 'Invoice' }, { key: 'customer', header: 'Customer' }, { key: 'date', header: 'Date' }, { key: 'due', header: 'Due' }, { key: 'status', header: 'Status' }, { key: 'total', header: 'Amount' }])); toast.success('Exported invoices.csv');
        }} className="gap-1.5 h-9"><Download className="w-3.5 h-3.5" />Export CSV</Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500">Open</div><div className="text-2xl font-extrabold text-blue-600">{fmt(totals.open)}</div></Card>
        <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500">Overdue</div><div className="text-2xl font-extrabold text-red-600">{fmt(totals.overdue)}</div></Card>
        <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500">Paid</div><div className="text-2xl font-extrabold text-emerald-600">{fmt(totals.paid)}</div></Card>
      </div>

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoices…" className="pl-9 h-10" data-testid="invoice-search-input" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Sent">Sent</SelectItem>
              <SelectItem value="Open">Open</SelectItem>
              <SelectItem value="Overdue">Overdue</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <BulkBar count={sel.count} onClear={sel.clear} note={fmt(pickedTotal)}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 gap-1 bg-white" data-testid="bulk-status-btn">Change status <ChevronDown className="w-3.5 h-3.5" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {STATUS_OPTIONS.map((s) => (
                  <DropdownMenuItem key={s} onClick={() => bulkStatus(s)} data-testid={`bulk-status-${s}`}>Mark as {s}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="outline" className="h-8 gap-1 bg-white" onClick={bulkDuplicate} data-testid="bulk-duplicate-btn"><Copy className="w-3.5 h-3.5" />Duplicate</Button>
            <Button size="sm" variant="outline" className="h-8 gap-1 bg-white text-red-600 hover:text-red-700" onClick={bulkDelete} data-testid="bulk-delete-btn"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
        </BulkBar>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left w-10"><TickBox checked={sel.allPicked} onChange={sel.toggleAll} label="Select every invoice listed" testId="select-all-invoices" /></th>
                <th className="px-5 py-3 text-left">Invoice</th><th className="px-5 py-3 text-left">Customer</th><th className="px-5 py-3 text-left">Date</th><th className="px-5 py-3 text-left">Due</th><th className="px-5 py-3 text-left">Status</th><th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((inv) => (
                <tr key={inv.id} className={`hover:bg-slate-50 ${sel.has(inv.id) ? 'bg-[#8BC53F]/5' : ''}`} data-testid={`acct-invoice-row-${inv.id}`}>
                  <td className="px-4 py-4"><TickBox checked={sel.has(inv.id)} onChange={() => sel.toggle(inv.id)} label={`Select invoice ${inv.id}`} testId={`select-invoice-${inv.id}`} /></td>
                  <td className="px-5 py-4"><button onClick={() => { setEditing(inv); setOpen(true); }} className="font-semibold text-[#6FA82C] hover:underline">{inv.id}</button></td>
                  <td className="px-5 py-4 font-medium text-slate-900">{custName(inv)}</td>
                  <td className="px-5 py-4 text-slate-600">{inv.date}</td>
                  <td className="px-5 py-4 text-slate-600">{inv.due}</td>
                  <td className="px-5 py-4"><Badge className={`${statusBadge(inv.status)} border-0`}>{inv.status}</Badge></td>
                  <td className="px-5 py-4 text-right font-bold text-slate-900 tabular-nums">{fmt(inv.total)}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => onDuplicate(inv)} title="Duplicate"><Copy className="w-4 h-4 text-slate-500" /></Button>
                      {String(inv.status).toLowerCase() !== 'paid' && <Button size="sm" variant="ghost" onClick={() => onCharge(inv)} title="Charge card"><CreditCard className="w-4 h-4 text-[#6FA82C]" /></Button>}
                      {String(inv.status).toLowerCase() !== 'paid' && <Button size="sm" variant="ghost" onClick={() => onMarkPaid(inv)} title="Mark Paid"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></Button>}
                      <Button size="sm" variant="ghost" onClick={() => onSend(inv)} title="Send"><Send className="w-4 h-4 text-slate-600" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(inv.id)} title="Delete"><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-500"><FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />No invoices match your filter</td></tr>}
            </tbody>
          </table>
          {filtered.length > 300 && <div className="px-5 py-3 text-xs text-slate-500 border-t border-slate-100">Showing the newest 300 of {filtered.length} invoices — search or filter to find older ones.</div>}
        </div>
      </Card>

      <InvoiceFormDialog open={open} setOpen={setOpen} editing={editing} customers={customers} allInvoices={invoices} onSaved={load} />
    </div>
  );
}
